import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, Volume2, VolumeX, Play, Pause, BadgeCheck, Shield } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import YouTubeEmbed from '@/components/YouTubeEmbed';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VideoCard = ({ video, isActive, onLike, user }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [timeLimitHit, setTimeLimitHit] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const isYouTube = video.youtube_url && !video.video_url;

  const isPremium = user?.is_premium || user?.subscription_tier === 'pro' || user?.subscription_tier === 'creator' || user?.subscription_tier === 'supremium';
  const maxPlaySeconds = isPremium ? 300 : 60; // 5 min premium, 1 min Viddy mode

  useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        setTimeLimitHit(false);
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {});
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isActive]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const remaining = Math.max(0, maxPlaySeconds - video.currentTime);
      setTimeLeft(Math.ceil(remaining));
      if (video.currentTime >= maxPlaySeconds) {
        video.pause();
        setIsPlaying(false);
        setTimeLimitHit(true);
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);
    return () => video.removeEventListener('timeupdate', onTimeUpdate);
  }, [maxPlaySeconds]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleDoubleTap = () => {
    if (!video.liked) {
      onLike(video.post_id);
    }
  };

  return (
    <div 
      className="relative w-full h-full bg-black snap-start flex items-center justify-center"
      onDoubleClick={handleDoubleTap}
      onClick={() => setShowControls(!showControls)}
    >
      {/* Video Content */}
      {isYouTube ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-full max-w-lg">
            <YouTubeEmbed url={video.youtube_url} />
          </div>
        </div>
      ) : video.video_url ? (
        <video
          ref={videoRef}
          src={`${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
          className="w-full h-full object-contain"
          muted={isMuted}
          playsInline
          controlsList="nodownload"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          data-testid="video-player"
        />
      ) : video.gif_url ? (
        <img 
          src={video.gif_url} 
          alt="GIF" 
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="flex items-center justify-center h-full p-8">
          <p className="text-white text-xl text-center">{video.content}</p>
        </div>
      )}

      {/* Play/Pause Overlay */}
      <AnimatePresence>
        {showControls && !isYouTube && video.video_url && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-black/30"
          >
            <button
              onClick={togglePlay}
              className="p-4 rounded-full bg-white/20 backdrop-blur-sm"
            >
              {isPlaying ? (
                <Pause className="w-12 h-12 text-white" />
              ) : (
                <Play className="w-12 h-12 text-white" />
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Time countdown */}
      {timeLeft !== null && timeLeft <= 10 && timeLeft > 0 && isPlaying && !timeLimitHit && (
        <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/70 rounded-full text-sm text-white font-bold z-20" data-testid="viddy-time-left">
          {timeLeft}s
        </div>
      )}

      {/* Time limit overlay */}
      {timeLimitHit && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 z-20" data-testid="viddy-limit-overlay">
          <Pause className="w-12 h-12 text-white mb-3" />
          <p className="text-white font-bold text-lg mb-1">Playback limit reached</p>
          <p className="text-white/70 text-sm">{isPremium ? '5 min premium limit' : '1 min preview · Upgrade for 5 min'}</p>
        </div>
      )}

      {/* Right Side Actions */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-6 z-10">
        {/* Like */}
        <button
          onClick={(e) => { e.stopPropagation(); onLike(video.post_id); }}
          className="flex flex-col items-center"
          data-testid="video-like-btn"
        >
          <div className={`p-3 rounded-full ${video.liked ? 'bg-red-500' : 'bg-white/20'} backdrop-blur-sm`}>
            <Heart className={`w-7 h-7 ${video.liked ? 'text-white fill-white' : 'text-white'}`} />
          </div>
          <span className="text-white text-sm font-bold mt-1 drop-shadow-lg">{video.likes_count || 0}</span>
        </button>

        {/* Comment */}
        <button className="flex flex-col items-center" data-testid="video-comment-btn">
          <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-sm font-bold mt-1 drop-shadow-lg">{video.comments_count || 0}</span>
        </button>

        {/* Share */}
        <button className="flex flex-col items-center" data-testid="video-share-btn">
          <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
            <Share2 className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-sm font-bold mt-1 drop-shadow-lg">{video.shares_count || 0}</span>
        </button>

        {/* Bookmark */}
        <button className="flex flex-col items-center" data-testid="video-bookmark-btn">
          <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
            <Bookmark className="w-7 h-7 text-white" />
          </div>
        </button>

        {/* Mute/Unmute */}
        {!isYouTube && video.video_url && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
            className="p-3 rounded-full bg-white/20 backdrop-blur-sm"
            data-testid="video-mute-btn"
          >
            {isMuted ? (
              <VolumeX className="w-6 h-6 text-white" />
            ) : (
              <Volume2 className="w-6 h-6 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Bottom User Info */}
      <div className="absolute left-4 right-20 bottom-8 z-10">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="w-12 h-12 border-2 border-white">
            <AvatarImage src={video.user?.avatar} />
            <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
              {video.user?.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-white font-bold text-lg drop-shadow-lg">{video.user?.name}</span>
              {video.user?.is_verified && (
                <BadgeCheck className="w-5 h-5 text-blue-400" />
              )}
              {video.user?.is_admin && (
                <Shield className="w-5 h-5 text-red-400" />
              )}
            </div>
            <span className="text-white/80 text-sm drop-shadow-lg">@{video.user?.username}</span>
          </div>
        </div>
        {video.content && (
          <p className="text-white text-sm drop-shadow-lg line-clamp-2">{video.content}</p>
        )}
        <p className="text-white/60 text-xs mt-2 drop-shadow-lg">
          {formatDistanceToNow(new Date(video.created_at), { addSuffix: true })}
        </p>
      </div>

      {/* Engagement Score Badge */}
      <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full z-10">
        <span className="text-white text-xs font-bold">🔥 {Math.round(video.engagement_score || 0)}</span>
      </div>
    </div>
  );
};

const TikTokFeed = ({ user }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API_URL}/feed/videos`, {
        withCredentials: true,
      });
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      const height = containerRef.current.clientHeight;
      const newIndex = Math.round(scrollTop / height);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
        // Record view
        if (videos[newIndex]) {
          axios.post(`${API_URL}/video/posts/${videos[newIndex].post_id}/view`, {}, { withCredentials: true }).catch(() => {});
        }
      }
    }
  }, [activeIndex, videos]);

  const handleLike = async (postId) => {
    const video = videos.find(v => v.post_id === postId);
    if (!video) return;

    try {
      if (video.liked) {
        await axios.delete(`${API_URL}/posts/${postId}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/posts/${postId}/like`, {}, { withCredentials: true });
      }
      setVideos(videos.map(v => 
        v.post_id === postId 
          ? { ...v, liked: !v.liked, likes_count: v.liked ? v.likes_count - 1 : v.likes_count + 1 }
          : v
      ));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center text-white p-8">
        <Play className="w-16 h-16 mb-4 opacity-50" />
        <h2 className="text-2xl font-bold mb-2">No Videos Yet</h2>
        <p className="text-white/60 text-center">Be the first to upload a video and go viral!</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-screen overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
      onScroll={handleScroll}
      style={{ scrollSnapType: 'y mandatory' }}
      data-testid="tiktok-feed"
    >
      {videos.map((video, index) => (
        <div 
          key={video.post_id} 
          className="h-screen w-full snap-start"
          style={{ scrollSnapAlign: 'start' }}
        >
          <VideoCard
            video={video}
            isActive={index === activeIndex}
            onLike={handleLike}
            user={user}
          />
        </div>
      ))}
    </div>
  );
};

export default TikTokFeed;
